import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Spinner, TabContent, TabPane } from "reactstrap";
import { useGetGroupsQuery, usePostGroupMutation, usePutGroupMutation } from "../../../../../backoffice/apis/groupsAPI";
import { Group } from "../../../../../backoffice/dtos/groups/group";
import DepartmentMeasurements, { validateDepartmentMeasurements } from "../../../../../backoffice/dtos/groups/DepartmentMeasurements";
import DepartmentSapParameter, { validateDepartmentSapParameters } from "../../../../../backoffice/dtos/groups/DepartmentSapParameter";
import DepartmentEdit from "./DepartmentEdit";
import DepartmentsMeasurementUnitsSettings from "./DepartmentsMeasurementUnitsSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import DepartmentsSapParameterEdit from "./DepartmentsSapParameterEdit";

export default function DepartmentEditModal(
    {
        isOpen,
        department,
        toggle
    }: {
        isOpen: boolean;
        department: Group | undefined;
        toggle: () => void;
    }
) {
    const { t } = useTranslation(['department_info', 'common'])

    let modalUi: JSX.Element;
    if (department) {
        modalUi = (
            <FetchDepartmentAndEditModal isOpen={isOpen} currentDepartment={department} toggle={toggle} />
        );
    } else {
        modalUi = (
            <InnerDepartmentEditModal isOpen={isOpen} currentDepartment={department} toggle={toggle} />
        );
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {t('Department', { ns: 'common' })}
            </ModalHeader>
            {modalUi}
        </Modal>
    )
}

function FetchDepartmentAndEditModal(
    {
        isOpen,
        currentDepartment,
        toggle
    }: {
        isOpen: boolean;
        currentDepartment: Group;
        toggle: () => void;
    }
) {
    const { data: group, isFetching: groupLoading, error: error } = useGetGroupsQuery({ groupIds: [currentDepartment.id], includeDetails: true }, { refetchOnMountOrArgChange: true })

    if (groupLoading) {
        return (
            <>
                <ModalBody className="d-flex flex-row justify-content-center">
                    <Spinner className="m-2" />
                </ModalBody>
            </>
        );
    } else if (error) {
        return (
            <>
                <ModalBody>
                    Failed to fetch data
                </ModalBody>
            </>
        );
    } else {
        return <InnerDepartmentEditModal isOpen={isOpen} currentDepartment={group!![0]} toggle={toggle} />
    }
}

function InnerDepartmentEditModal(
    {
        isOpen,
        currentDepartment,
        toggle
    }: {
        isOpen: boolean;
        currentDepartment: Group | undefined;
        toggle: () => void;
    }
) {
    const [putGroup] = usePutGroupMutation();
    const [postGroup] = usePostGroupMutation();

    const [selectedTab, setSelectedTab] = useState(0)
    const [department, setDepartment] = useState<Group>({ id: 0, description: "" })
    const [sending, setSending] = useState(false);

    const [errors, setErrors] = useState<Map<string, any>>()

    const { t } = useTranslation(['department_info', 'common'])

    useEffect(() => {
        const measurementUnits: DepartmentMeasurements = {
            groupId: currentDepartment?.id ?? 0,
            primaryMeasurementUnit: currentDepartment?.measurements?.primaryMeasurementUnit,
            predictionMeasurementUnit: currentDepartment?.measurements?.predictionMeasurementUnit,
            actualInputMeasurementUnit: currentDepartment?.measurements?.actualInputMeasurementUnit,
            calculatedMeasurementUnit: currentDepartment?.measurements?.calculatedMeasurementUnit,
            secondaryCalculatedMeasurementUnit: currentDepartment?.measurements?.secondaryCalculatedMeasurementUnit
        }
        const sapParameters: DepartmentSapParameter[] = currentDepartment?.sapParameters?.map(m => ({
            groupId: m.groupId,
            fieldName: m.fieldName,
            value: m.value
        })) ?? [];

        const newDepartment = {
            id: currentDepartment?.id ?? 0,
            description: currentDepartment?.description ?? "",
            measurements: measurementUnits,
            sapParameters: sapParameters
        }

        setDepartment(newDepartment);
    }, [currentDepartment]);

    const save = useCallback(async () => {
        try {
            setSending(true)
            const errors = new Map<string, any>();
            if (department.description === "") {
                errors.set("department", new Map([["name", "This field is required!"]]))
            }

            let errorsDepartmentMeasurements = validateDepartmentMeasurements(department.measurements);
            if (errorsDepartmentMeasurements !== undefined) {
                errors.set("measurements", errorsDepartmentMeasurements);
            }

            let errorsDepartmentSapMeasurements = validateDepartmentSapParameters(department.sapParameters)

            if (errorsDepartmentSapMeasurements !== undefined) {
                errors.set("sapParameters", errorsDepartmentSapMeasurements);
            }

            if (errors.size > 0) {
                setErrors(errors)
                return;
            }

            if (currentDepartment) {
                await putGroup(department).unwrap()
            } else {
                await postGroup(department).unwrap()
            }

            toggle()
        } catch (e: any) {
            toast(e.data)
        } finally {
            setSending(false)
        }
    }, [department])

    const setDepartmentMeasurements = useCallback((callback: (departmentMeasurement: DepartmentMeasurements) => DepartmentMeasurements) => {
        if (!department.measurements)
            return;

        const response = callback(department.measurements!!)

        setDepartment(ref => ({ ...ref, measurements: response }))
    }, [department]);

    const departmentMeasurements = useMemo(() => {
        return department.measurements;
    }, [department]);

    const setDepartmentSapArguments = useCallback((callback: (departmentSapArguments: DepartmentSapParameter[]) => DepartmentSapParameter[]) => {
        if (!department.sapParameters)
            return;

        const response = callback(department.sapParameters!!)

        setDepartment(ref => ({ ...ref, sapParameters: response }))
    }, [department]);

    const departmentSapParameters = useMemo(() => {
        return department.sapParameters;
    }, [department]);

    return (
        <>
            <ModalBody>
                <Nav tabs>
                    <NavItem onClick={() => setSelectedTab(0)}>
                        <NavLink active={selectedTab == 0}>
                            Information {errors?.has("department") === true && <FontAwesomeIcon icon={faExclamationCircle} size="1x" color="red"/>}
                        </NavLink>
                    </NavItem>
                    <NavItem active={selectedTab == 1} onClick={() => setSelectedTab(1)}>
                        <NavLink active={selectedTab == 1}>
                            Measurement Units {errors?.has("measurements") === true && <FontAwesomeIcon icon={faExclamationCircle} size="1x" color="red"/>}
                        </NavLink>
                    </NavItem>
                    <NavItem active={selectedTab == 2} onClick={() => setSelectedTab(2)}>
                        <NavLink active={selectedTab == 2}>
                            Sap Arguments {errors?.has("sapParameters") === true && <FontAwesomeIcon icon={faExclamationCircle} size="1x" color="red"/>}
                        </NavLink>
                    </NavItem>
                </Nav>
                <TabContent activeTab={selectedTab} className="mt-3">
                    <TabPane tabId={0}>
                        {selectedTab === 0 && <DepartmentEdit isOpen={isOpen && selectedTab === 0} department={department} setDepartment={setDepartment} toggle={toggle} errors={errors?.get("department") as Map<string, string>}/>}
                    </TabPane>
                    <TabPane tabId={1}>
                        {
                            selectedTab === 1 && (
                                departmentMeasurements
                                    ? <DepartmentsMeasurementUnitsSettings departmentsMeasurements={departmentMeasurements} setDepartmentsMeasurements={setDepartmentMeasurements} toggle={toggle} errors={errors?.get("measurements") as Map<string, string>} group={department}/>
                                    : <></>
                            )
                        }
                    </TabPane>
                    <TabPane tabId={2}>
                        {
                            selectedTab === 2 && (
                                departmentSapParameters
                                    ? <DepartmentsSapParameterEdit isOpen={isOpen && selectedTab === 2} group={department} sapParameters={departmentSapParameters} setSapParameters={setDepartmentSapArguments} toggle={toggle} errors={errors?.get("sapParameters") as Map<string, string>}/>
                                    : <></>
                            )
                        }
                    </TabPane>
                </TabContent>
            </ModalBody>
            <ModalFooter>
                <Button color='primary' onClick={() => save()} disabled={sending}>
                    <span className='fa fa-lg fa-save me-2' />{t('Save', { ns: 'common' })}
                    {sending && <Spinner size="sm" className="ms-2" />}
                </Button>
            </ModalFooter>
        </>
    )
}
